import React, { useState, useEffect } from 'react';
import { Header } from '../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { mentionsAPI } from '../lib/api';
import { formatDateTime } from '../lib/utils';
import { MessageCircle, Plus, Trash2, RefreshCw, TrendingUp, TrendingDown, Minus, ExternalLink, Search } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const SENTIMENTOS = [
    { value: 'positivo', label: 'Positivo', color: 'bg-green-100 text-green-700', icon: TrendingUp },
    { value: 'neutro', label: 'Neutro', color: 'bg-slate-100 text-slate-700', icon: Minus },
    { value: 'negativo', label: 'Negativo', color: 'bg-red-100 text-red-700', icon: TrendingDown },
];

const FONTES = [
    { value: 'twitter', label: 'Twitter/X' },
    { value: 'facebook', label: 'Facebook' },
    { value: 'instagram', label: 'Instagram' },
    { value: 'youtube', label: 'YouTube' },
    { value: 'tiktok', label: 'TikTok' },
    { value: 'portal', label: 'Portal de Notícias' },
    { value: 'blog', label: 'Blog' },
    { value: 'jornal', label: 'Jornal' },
    { value: 'revista', label: 'Revista' },
    { value: 'radio', label: 'Rádio' },
    { value: 'tv', label: 'TV' },
    { value: 'outro', label: 'Outro' },
];

const initialForm = {
    query: '',
    titulo: '',
    fonte: '',
    url: '',
    sentimento: '',
    resumo: '',
    data: '',
};

export const Mentions = () => {
    const [mentions, setMentions] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [form, setForm] = useState(initialForm);
    const [filterSentiment, setFilterSentiment] = useState('');

    useEffect(() => {
        loadData();
    }, [filterSentiment]);

    const loadData = async () => {
        setLoading(true);
        try {
            const params = filterSentiment ? { sentimento: filterSentiment } : {};
            const [mentionsRes, statsRes] = await Promise.all([
                mentionsAPI.list(params),
                mentionsAPI.stats()
            ]);
            setMentions(mentionsRes.data.mentions);
            setStats(statsRes.data);
        } catch (error) {
            toast.error('Erro ao carregar menções');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async () => {
        if (!form.query || !form.titulo || !form.fonte || !form.url || !form.sentimento) {
            toast.error('Preencha todos os campos obrigatórios');
            return;
        }
        try {
            await mentionsAPI.create(form);
            toast.success('Menção registrada!');
            setDialogOpen(false);
            setForm(initialForm);
            loadData();
        } catch (error) {
            toast.error('Erro ao registrar menção');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Tem certeza que deseja excluir esta menção?')) return;
        try {
            await mentionsAPI.delete(id);
            toast.success('Menção removida!');
            loadData();
        } catch (error) {
            toast.error('Erro ao excluir menção');
        }
    };

    const getSentimentBadge = (sentimento) => {
        const config = SENTIMENTOS.find(s => s.value === sentimento) || SENTIMENTOS[1];
        const Icon = config.icon;
        return (
            <Badge className={config.color}>
                <Icon className="w-3 h-3 mr-1" />
                {config.label}
            </Badge>
        );
    };

    const getFonteLabel = (value) => FONTES.find(f => f.value === value)?.label || value;

    const getSentimentCount = (sentiment) => {
        const item = stats?.by_sentiment?.find(s => s._id === sentiment);
        return item?.count || 0;
    };

    return (
        <div data-testid="mentions-page">
            <Header 
                title="Monitoramento de Menções" 
                subtitle="Acompanhe menções e reputação online"
                actions={
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={loadData} disabled={loading} data-testid="refresh-mentions">
                            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                            Atualizar
                        </Button>
                        <Dialog open={dialogOpen} onOpenChange={(open) => {
                            setDialogOpen(open);
                            if (!open) setForm(initialForm);
                        }}>
                            <DialogTrigger asChild>
                                <Button data-testid="add-mention-btn">
                                    <Plus className="w-4 h-4 mr-2" />
                                    Nova Menção
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-lg">
                                <DialogHeader>
                                    <DialogTitle>Registrar Nova Menção</DialogTitle>
                                </DialogHeader>
                                <div className="space-y-4 mt-4">
                                    <div className="space-y-2">
                                        <Label>Termo de Busca (Query) *</Label>
                                        <Input
                                            value={form.query}
                                            onChange={(e) => setForm({ ...form, query: e.target.value })}
                                            placeholder="Ex: João Silva prefeito"
                                            data-testid="mention-query"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Título da Menção *</Label>
                                        <Input
                                            value={form.titulo}
                                            onChange={(e) => setForm({ ...form, titulo: e.target.value })}
                                            placeholder="Título ou manchete"
                                            data-testid="mention-titulo"
                                        />
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label>Fonte *</Label>
                                            <Select value={form.fonte} onValueChange={(value) => setForm({ ...form, fonte: value })}>
                                                <SelectTrigger data-testid="mention-fonte">
                                                    <SelectValue placeholder="Selecione" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {FONTES.map(fonte => (
                                                        <SelectItem key={fonte.value} value={fonte.value}>
                                                            {fonte.label}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Sentimento *</Label>
                                            <Select value={form.sentimento} onValueChange={(value) => setForm({ ...form, sentimento: value })}>
                                                <SelectTrigger data-testid="mention-sentimento">
                                                    <SelectValue placeholder="Selecione" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {SENTIMENTOS.map(s => (
                                                        <SelectItem key={s.value} value={s.value}>
                                                            {s.label}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>URL *</Label>
                                        <Input
                                            value={form.url}
                                            onChange={(e) => setForm({ ...form, url: e.target.value })}
                                            placeholder="https://exemplo.com/noticia"
                                            data-testid="mention-url"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Data (opcional)</Label>
                                        <Input
                                            type="datetime-local"
                                            value={form.data}
                                            onChange={(e) => setForm({ ...form, data: e.target.value })}
                                            data-testid="mention-data"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Resumo (opcional)</Label>
                                        <Textarea
                                            value={form.resumo}
                                            onChange={(e) => setForm({ ...form, resumo: e.target.value })}
                                            placeholder="Breve resumo do conteúdo"
                                            rows={3}
                                            data-testid="mention-resumo"
                                        />
                                    </div>
                                    <Button onClick={handleSubmit} className="w-full" data-testid="mention-submit">
                                        Registrar Menção
                                    </Button>
                                </div>
                            </DialogContent>
                        </Dialog>
                    </div>
                }
            />
            
            <div className="p-8">
                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                    <Card className="stat-card">
                        <CardContent className="p-6">
                            <div className="flex items-center gap-4">
                                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                                    <MessageCircle className="w-5 h-5 text-blue-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-slate-500">Total</p>
                                    <p className="text-2xl font-bold font-heading">{stats?.total || 0}</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="stat-card cursor-pointer hover:border-green-300" onClick={() => setFilterSentiment(filterSentiment === 'positivo' ? '' : 'positivo')}>
                        <CardContent className="p-6">
                            <div className="flex items-center gap-4">
                                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                                    <TrendingUp className="w-5 h-5 text-green-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-slate-500">Positivas</p>
                                    <p className="text-2xl font-bold font-heading">{getSentimentCount('positivo')}</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="stat-card cursor-pointer hover:border-slate-300" onClick={() => setFilterSentiment(filterSentiment === 'neutro' ? '' : 'neutro')}>
                        <CardContent className="p-6">
                            <div className="flex items-center gap-4">
                                <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                                    <Minus className="w-5 h-5 text-slate-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-slate-500">Neutras</p>
                                    <p className="text-2xl font-bold font-heading">{getSentimentCount('neutro')}</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="stat-card cursor-pointer hover:border-red-300" onClick={() => setFilterSentiment(filterSentiment === 'negativo' ? '' : 'negativo')}>
                        <CardContent className="p-6">
                            <div className="flex items-center gap-4">
                                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                                    <TrendingDown className="w-5 h-5 text-red-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-slate-500">Negativas</p>
                                    <p className="text-2xl font-bold font-heading">{getSentimentCount('negativo')}</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Filter indicator */}
                {filterSentiment && (
                    <div className="mb-4 flex items-center gap-2">
                        <span className="text-sm text-slate-500">Filtrando por:</span>
                        {getSentimentBadge(filterSentiment)}
                        <Button size="sm" variant="ghost" onClick={() => setFilterSentiment('')}>
                            Limpar filtro
                        </Button>
                    </div>
                )}

                {/* Mentions Table */}
                <Card>
                    <CardHeader>
                        <CardTitle className="font-heading">Menções Recentes</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Título</TableHead>
                                    <TableHead>Fonte</TableHead>
                                    <TableHead>Sentimento</TableHead>
                                    <TableHead>Query</TableHead>
                                    <TableHead>Data</TableHead>
                                    <TableHead>Ações</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {mentions.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                                            Nenhuma menção registrada
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    mentions.map((mention) => (
                                        <TableRow key={mention.id}>
                                            <TableCell className="font-medium max-w-[250px]">
                                                <div className="truncate" title={mention.titulo}>
                                                    {mention.titulo}
                                                </div>
                                                {mention.resumo && (
                                                    <p className="text-xs text-slate-400 truncate mt-1">{mention.resumo}</p>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant="outline">{getFonteLabel(mention.fonte)}</Badge>
                                            </TableCell>
                                            <TableCell>
                                                {getSentimentBadge(mention.sentimento)}
                                            </TableCell>
                                            <TableCell className="text-sm text-slate-500">
                                                <div className="flex items-center gap-1">
                                                    <Search className="w-3 h-3" />
                                                    {mention.query}
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-sm text-slate-500">
                                                {formatDateTime(mention.data)}
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex gap-1">
                                                    <Button 
                                                        size="icon" 
                                                        variant="ghost" 
                                                        onClick={() => window.open(mention.url, '_blank')}
                                                        title="Abrir link"
                                                    >
                                                        <ExternalLink className="w-4 h-4" />
                                                    </Button>
                                                    <Button 
                                                        size="icon" 
                                                        variant="ghost" 
                                                        className="text-red-600 hover:text-red-700"
                                                        onClick={() => handleDelete(mention.id)}
                                                        data-testid={`delete-mention-${mention.id}`}
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default Mentions;
